import { create } from 'zustand';

/**
 * Confirmation "le client va voir ce post" — affichée quand un agent passe un post en "À valider"
 * (statut to_validate = visible sur l'espace client). Throttle 30 min PAR CLIENT : si on enchaîne
 * plusieurs posts du même client, on ne re-demande pas. Logique centrale → vaut pour tous les agents.
 */
const THROTTLE_MS = 30 * 60 * 1000;
const LS_KEY = 'nudge-clientval-confirmed';

function loadSeen(): Record<string, number> {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}') || {}; } catch { return {}; }
}
function saveSeen(map: Record<string, number>): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

interface ConfirmClientStore {
  open: boolean;
  clientName: string;
  _resolve: ((ok: boolean) => void) | null;
  _clientId: string;
  /** Demande la confirmation. Résout `true` direct si déjà confirmé pour ce client il y a < 30 min. */
  request: (opts: { clientId: string; clientName: string }) => Promise<boolean>;
  confirm: () => void;
  cancel: () => void;
}

export const useConfirmClientStore = create<ConfirmClientStore>((set, get) => ({
  open: false,
  clientName: '',
  _resolve: null,
  _clientId: '',
  request: ({ clientId, clientName }) => {
    const key = clientId || 'unknown';
    const seen = loadSeen();
    if (Date.now() - (seen[key] || 0) < THROTTLE_MS) {
      return Promise.resolve(true); // déjà confirmé récemment pour ce client → pas de popup
    }
    return new Promise<boolean>((resolve) => {
      set({ open: true, clientName: clientName || 'le client', _clientId: key, _resolve: resolve });
    });
  },
  confirm: () => {
    const { _resolve, _clientId } = get();
    const seen = loadSeen();
    seen[_clientId] = Date.now();
    saveSeen(seen);
    set({ open: false, _resolve: null, _clientId: '' });
    _resolve?.(true);
  },
  cancel: () => {
    const { _resolve } = get();
    set({ open: false, _resolve: null, _clientId: '' });
    _resolve?.(false);
  },
}));
